import { useAtomValue } from "jotai";
import { Edit, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { apiService } from "@/api/service";
import { useToast } from "@/components";
import ConfirmModal from "@/components/ConfirmModal";
import Modal from "@/components/Modal";
import Button from "@/components/ui/Button";
import { accountUserTreeAtom } from "@/store";

type ListedUser = {
  id: string; // account_user id
  role: string;
  start_date: string | null;
  end_date: string | null;
  user: {
    id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
};

export function ManageUsersModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Gerenciar Usuários" size="md">
      <ManageUsers onClose={onClose} />
    </Modal>
  );
}

export function ManageUsers({ onClose }: { onClose?: () => void }) {
  const { showSuccess, showWarning, showError } = useToast();
  const accountUserTree = useAtomValue(accountUserTreeAtom);
  const currentAccountId = useMemo(() => accountUserTree?.account?.id || null, [accountUserTree]);
  const isAdmin = (accountUserTree?.role || "") === "admin";

  const [users, setUsers] = useState<ListedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add modal state
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Edit/remove modal state
  const [editing, setEditing] = useState<ListedUser | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchUsers = async () => {
    if (!currentAccountId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await apiService.accountUser.query({
        filter: { account: { _eq: currentAccountId } } as any,
        fields: ["id", "role", "start_date", "end_date", "user.id", "user.email", "user.first_name", "user.last_name"],
        limit: -1,
      } as any);
      setUsers(
        (rows as any as ListedUser[]).sort((a, b) => {
          const an = (a.user?.first_name || "") + (a.user?.last_name || "");
          const bn = (b.user?.first_name || "") + (b.user?.last_name || "");
          return an.localeCompare(bn);
        })
      );
    } catch (e: any) {
      setError(e?.message || "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccountId]);

  const handleOpenAdd = () => {
    if (!isAdmin) {
      showWarning({
        message: "Apenas administradores podem gerenciar usuários.",
        position: "center",
      });
      return;
    }
    setAddEmail("");
    setIsAddOpen(true);
  };

  const handleAddConfirm = async () => {
    if (!currentAccountId) return false;
    const email = addEmail.trim().toLowerCase();
    if (!email) {
      showWarning({ message: "Informe um e-mail válido.", position: "center" });
      return false;
    }
    setSubmitting(true);
    try {
      // Check if user already in list
      const already = users.some((u) => u.user?.email?.toLowerCase() === email);
      if (already) {
        showWarning({
          message: "Usuário já possui acesso a esta conta.",
          position: "center",
        });
        return false;
      }

      // Find user by email
      const found = await apiService.user.query({
        filter: { email: { _eq: email } },
        fields: ["id", "email", "first_name", "last_name"],
        limit: 1,
      });

      if (!found || found.length === 0) {
        showError({
          message: "Usuário não encontrado para este e-mail.",
          position: "center",
        });
        return false;
      }

      const user = found[0] as any as { id: string };

      await apiService.accountUser.create({
        account: currentAccountId,
        user: user.id,
        role: "admin", // only admin supported for now
        start_date: new Date().toISOString(),
        end_date: null,
      });

      showSuccess({ message: "Usuário adicionado com sucesso." });
      await fetchUsers();
    } catch (e: any) {
      showError({ message: e?.message || "Erro ao adicionar usuário" });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (row: ListedUser) => {
    setEditing(row);
  };

  const handleSaveEdit = async () => {
    if (!editing) return false;
    try {
      // Only admin role supported; no-op but keep update for future-proofing
      await apiService.accountUser.update(editing.id, {
        role: editing.role,
      } as any);
      showSuccess({ message: "Usuário atualizado." });
      await fetchUsers();
    } catch (e: any) {
      showError({
        message: e?.message || "Erro ao atualizar usuário",
        position: "center",
      });
      return false;
    }
  };

  const handleRemove = (id: string) => {
    setDeleteId(id);
  };

  const confirmRemove = async () => {
    if (!deleteId) return false;
    try {
      await apiService.accountUser.update(deleteId, {
        end_date: new Date().toISOString(),
      });
      showSuccess({ message: "Usuário removido." });
      setDeleteId(null);
      await fetchUsers();
    } catch (e: any) {
      showError({
        message: e?.message || "Erro ao remover usuário",
        position: "center",
      });
      return false;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-md font-semibold text-gray-900">Usuários com acesso</h3>
        {isAdmin && (
          <Button size="sm" variant="primary" onClick={handleOpenAdd}>
            Adicionar Usuário
          </Button>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-500 py-8">Carregando...</div>
      ) : users.length === 0 ? (
        <div className="text-center text-gray-500 py-8">Nenhum usuário com acesso.</div>
      ) : (
        <div className="space-y-2">
          {users.map((row) => {
            const name = `${row.user?.first_name || ""}${row.user?.last_name ? ` ${row.user?.last_name}` : ""}`.trim();
            const email = row.user?.email || "-";
            return (
              <div key={row.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-gray-900">{name || email}</div>
                  <div className="text-xs text-gray-500">{email}</div>
                  <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">{row.role}</span>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleEdit(row)}
                        icon={<Edit size={16} />}
                      ></Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRemove(row.id)}
                        icon={<Trash2 size={16} />}
                      ></Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add User Modal */}
      <ConfirmModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onConfirm={handleAddConfirm}
        title="Adicionar Usuário"
        confirmText="Adicionar"
        cancelText="Cancelar"
        variant="primary"
        confirmEnabled={!submitting}
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
            <input
              type="email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
              placeholder="usuario@exemplo.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Papel</label>
            <input
              type="text"
              value="admin"
              disabled
              className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-600"
            />
            <p className="text-xs text-gray-500 mt-1">Apenas papel "admin" é suportado no momento.</p>
          </div>
        </div>
      </ConfirmModal>

      {/* Edit User Modal */}
      <ConfirmModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        onConfirm={handleSaveEdit}
        title="Editar Usuário"
        confirmText="Salvar"
        cancelText="Cancelar"
        variant="primary"
      >
        {editing && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Usuário</label>
              <input
                type="text"
                value={
                  `${editing.user?.first_name || ""}${
                    editing.user?.last_name ? ` ${editing.user?.last_name}` : ""
                  }`.trim() ||
                  editing.user?.email ||
                  ""
                }
                disabled
                className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Papel</label>
              <input
                type="text"
                value="admin"
                disabled
                className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-600"
              />
              <p className="text-xs text-gray-500 mt-1">Apenas papel "admin" é suportado no momento.</p>
            </div>
            <div className="pt-2">
              <Button variant="destructive" onClick={() => handleRemove(editing.id)} className="w-full">
                Remover Usuário
              </Button>
            </div>
          </div>
        )}
      </ConfirmModal>

      {/* Remove confirmation */}
      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmRemove}
        title="Remover Usuário"
        message="Tem certeza que deseja remover o acesso deste usuário?"
        confirmText="Remover"
        cancelText="Cancelar"
        variant="danger"
      />
    </div>
  );
}

export default ManageUsersModal;
